import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  useTheme,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  AdminPanelSettings,
  TrendingUp,
  People,
  Assessment,
  SwapHoriz,
} from '@mui/icons-material';
import { useAuth } from '@store/authStore';
import { useNavigate } from 'react-router-dom';
import httpClient from '@utils/httpClient';
import { getDefaultRouteForRole } from '@config/roles';
import { resetAllStores } from '@store/storeResetUtils';

interface SystemStat {
  label: string;
  value: string | number;
  icon: React.ReactElement;
  color: string;
}

interface ActivityLog {
  id: number;
  timestamp: string;
  user: string;
  action: string;
  status: 'success' | 'warning' | 'error';
}

interface Role {
  id: string;
  role_name: string;
  role_detail: string;
}

const SuperAdminDashboard: React.FC = () => {
  const theme = useTheme();
  const { user, login } = useAuth();
  const navigate = useNavigate();

  const [roles, setRoles] = useState<Role[]>([]);
  const [selectedRoleId, setSelectedRoleId] = useState<string>('');
  const [isSwitching, setIsSwitching] = useState(false);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');

  // Dummy data for system statistics
  const systemStats: SystemStat[] = [
    {
      label: 'Total Users',
      value: 1247,
      icon: <People sx={{ fontSize: 40 }} />,
      color: '#2196F3',
    },
    {
      label: 'Active Sessions',
      value: 342,
      icon: <TrendingUp sx={{ fontSize: 40 }} />,
      color: '#4CAF50',
    },
    {
      label: 'System Health',
      value: '98.5%',
      icon: <Assessment sx={{ fontSize: 40 }} />,
      color: '#FF9800',
    },
    {
      label: 'Admin Actions',
      value: 89,
      icon: <AdminPanelSettings sx={{ fontSize: 40 }} />,
      color: '#9C27B0',
    },
  ];

  // Dummy data for recent activity
  const recentActivity: ActivityLog[] = [
    {
      id: 1,
      timestamp: '2025-11-05 10:30 AM',
      user: 'John Doe',
      action: 'Created new user account',
      status: 'success',
    },
    {
      id: 2,
      timestamp: '2025-11-05 10:15 AM',
      user: 'Jane Smith',
      action: 'Updated system settings',
      status: 'success',
    },
    {
      id: 3,
      timestamp: '2025-11-05 09:45 AM',
      user: 'Mike Johnson',
      action: 'Failed login attempt',
      status: 'warning',
    },
    {
      id: 4,
      timestamp: '2025-11-05 09:30 AM',
      user: 'Sarah Williams',
      action: 'Deleted user account',
      status: 'error',
    },
    {
      id: 5,
      timestamp: '2025-11-05 09:00 AM',
      user: 'Tom Brown',
      action: 'Updated role permissions',
      status: 'success',
    },
  ];

  // Fetch available roles on component mount
  useEffect(() => {
    const fetchRoles = async () => {
      try {
        const data = await httpClient.get('/super-admin/roles');
        if (data.success) {
          setRoles(data.roles);
        }
      } catch (err) {
        console.error('Error fetching roles:', err);
        setError('Failed to load roles');
      }
    };

    fetchRoles();
  }, []);

  const handleRoleSwitch = async () => {
    if (!selectedRoleId) {
      setError('Please select a role');
      return;
    }

    setIsSwitching(true);
    setError('');
    setSuccess('');

    try {
      const data = await httpClient.post('/super-admin/switch-role', {
        roleId: selectedRoleId,
      });

      if (data.success) {
        // Reset all stores before switching role to ensure clean state
        console.log('🔄 SuperAdmin: Resetting all stores before role switch...');
        resetAllStores();

        setSuccess(`Role switched to ${data.user.role} successfully!`);

        // Update the user data in auth store
        const userData = {
          id: data.user.id,
          email: data.user.email,
          firstName: data.user.firstName || data.user.name?.split(' ')[0] || '',
          lastName: data.user.lastName || data.user.name?.split(' ').slice(1).join(' ') || '',
          name: data.user.name,
          contact: data.user.contact || '',
          profilePhoto: data.user.profile_photo
            ? (data.user.profile_photo.startsWith('http')
              ? data.user.profile_photo
              : `http://localhost:5000${data.user.profile_photo}`)
            : '',
          role: data.user.role,
          createdAt: data.user.created_at || new Date().toISOString(),
          flag_daily_update: data.user.flag_daily_update,
          is_super_admin: data.user.is_super_admin || 0,
        };

        // Update auth store with new user data and token
        login(userData, data.token);

        // Wait a moment to show success message
        setTimeout(() => {
          const defaultRoute = getDefaultRouteForRole(data.user.role);
          navigate(defaultRoute, { replace: true });
        }, 1500);
      }
    } catch (err: any) {
      console.error('Error switching role:', err);
      setError(err.message || 'Failed to switch role');
      setIsSwitching(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'success':
        return 'success';
      case 'warning':
        return 'warning';
      case 'error':
        return 'error';
      default:
        return 'default';
    }
  };

  return (
    <Box
      sx={{
        p: 4,
        minHeight: '100vh',
        background: theme.palette.mode === 'dark'
          ? 'linear-gradient(135deg, #0a0a0a 0%, #1a1a2e 100%)'
          : 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
      }}
    >
      {/* Header Section */}
      <Box
        sx={{
          mb: 5,
          p: 4,
          borderRadius: 3,
          background: theme.palette.mode === 'dark'
            ? 'linear-gradient(135deg, rgba(139, 92, 246, 0.1) 0%, rgba(59, 130, 246, 0.1) 100%)'
            : 'linear-gradient(135deg, rgba(139, 92, 246, 0.08) 0%, rgba(59, 130, 246, 0.08) 100%)',
          border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(139, 92, 246, 0.2)' : 'rgba(139, 92, 246, 0.15)'}`,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <AdminPanelSettings
            sx={{
              fontSize: 40,
              mr: 2,
              color: theme.palette.mode === 'dark' ? '#8b5cf6' : '#7c3aed',
            }}
          />
          <Box>
            <Typography
              variant="h3"
              sx={{
                fontWeight: 700,
                background: theme.palette.mode === 'dark'
                  ? 'linear-gradient(135deg, #8b5cf6 0%, #3b82f6 100%)'
                  : 'linear-gradient(135deg, #7c3aed 0%, #2563eb 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
              }}
            >
              Role Command Center
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: theme.palette.text.secondary,
                mt: 0.5,
                fontSize: '1.1rem',
              }}
            >
              Switch between roles to experience different perspectives and permissions
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Role Switcher Card */}
      <Card
        sx={{
          mb: 4,
          borderRadius: 3,
          background: theme.palette.mode === 'dark'
            ? 'linear-gradient(135deg, #1e1e2e 0%, #2a2a3e 100%)'
            : 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
          border: `1px solid ${theme.palette.divider}`,
          boxShadow: theme.palette.mode === 'dark'
            ? '0 8px 32px rgba(0, 0, 0, 0.4)'
            : '0 8px 32px rgba(0, 0, 0, 0.08)',
        }}
      >
        <CardContent sx={{ p: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <Box
              sx={{
                width: 56,
                height: 56,
                borderRadius: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: theme.palette.mode === 'dark'
                  ? 'linear-gradient(135deg, #8b5cf6 0%, #3b82f6 100%)'
                  : 'linear-gradient(135deg, #7c3aed 0%, #2563eb 100%)',
                mr: 2,
              }}
            >
              <SwapHoriz sx={{ fontSize: 32, color: '#ffffff' }} />
            </Box>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
                Role Switcher
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Instantly switch to any role and explore the system from different user perspectives
              </Typography>
            </Box>
          </Box>

          {error && (
            <Alert
              severity="error"
              sx={{
                mb: 3,
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'error.main',
              }}
              onClose={() => setError('')}
            >
              {error}
            </Alert>
          )}

          {success && (
            <Alert
              severity="success"
              sx={{
                mb: 3,
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'success.main',
              }}
              onClose={() => setSuccess('')}
            >
              {success}
            </Alert>
          )}

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: 'auto 1fr auto' },
              gap: 2,
              alignItems: 'center',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Chip
                label={`Current: ${user?.role || 'Unknown'}`}
                color="primary"
                sx={{
                  fontWeight: 600,
                  fontSize: '0.9rem',
                  py: 2.5,
                  px: 1,
                }}
              />
            </Box>

            <FormControl
              fullWidth
              disabled={isSwitching}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                },
              }}
            >
              <InputLabel id="role-select-label">Select New Role</InputLabel>
              <Select
                labelId="role-select-label"
                value={selectedRoleId}
                label="Select New Role"
                onChange={(e) => setSelectedRoleId(e.target.value)}
              >
                <MenuItem value="">
                  <em>Choose a role to switch to</em>
                </MenuItem>
                {roles.map((role) => (
                  <MenuItem key={role.id} value={role.id}>
                    <Box>
                      <Typography variant="body1" fontWeight={600}>
                        {role.role_name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {role.role_detail}
                      </Typography>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Button
              variant="contained"
              onClick={handleRoleSwitch}
              disabled={!selectedRoleId || isSwitching}
              startIcon={isSwitching ? <CircularProgress size={20} color="inherit" /> : <SwapHoriz />}
              sx={{
                px: 4,
                py: 1.8,
                borderRadius: 2,
                textTransform: 'none',
                fontSize: '1rem',
                fontWeight: 700,
                background: theme.palette.mode === 'dark'
                  ? 'linear-gradient(135deg, #8b5cf6 0%, #3b82f6 100%)'
                  : 'linear-gradient(135deg, #7c3aed 0%, #2563eb 100%)',
                boxShadow: '0 4px 12px rgba(139, 92, 246, 0.3)',
                '&:hover': {
                  background: theme.palette.mode === 'dark'
                    ? 'linear-gradient(135deg, #7c3aed 0%, #2563eb 100%)'
                    : 'linear-gradient(135deg, #6d28d9 0%, #1d4ed8 100%)',
                  boxShadow: '0 6px 16px rgba(139, 92, 246, 0.4)',
                },
                '&:disabled': {
                  background: theme.palette.action.disabledBackground,
                  boxShadow: 'none',
                },
              }}
            >
              {isSwitching ? 'Switching...' : 'Switch Role'}
            </Button>
          </Box>

          {/* Info Box */}
          <Box
            sx={{
              mt: 3,
              p: 2,
              borderRadius: 2,
              background: theme.palette.mode === 'dark'
                ? 'rgba(139, 92, 246, 0.05)'
                : 'rgba(139, 92, 246, 0.03)',
              border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(139, 92, 246, 0.1)' : 'rgba(139, 92, 246, 0.08)'}`,
            }}
          >
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
              ℹ️ <strong>Note:</strong> Your super admin privileges remain intact regardless of role.
              You can switch back anytime from this page.
            </Typography>
          </Box>
        </CardContent>
      </Card>

    </Box>
  );
};

export default SuperAdminDashboard;
